import {message} from "antd";

export const trimValue = (value: string): string => {
    return value.trim().replaceAll(' ', '')
}

export const numberValidator = (value: string, columnName: string): boolean => {
    if (!value) return false;

    if (value.length > 12) {
        message.error('The input exceeds the maximum length. Please enter a number with fewer than 12 characters.');
        return false;
    }

    const trimmedValue = value.trim().replaceAll(' ', '');

    const colName = columnName? ' "' + columnName + '"' : '';

    if (isNaN(parseFloat(trimmedValue))) {
        message.error(`Please enter a valid${colName} number.`);
        return false;
    }

    const numberPattern = /^-?\d*\.?\d+$/;

    if (!numberPattern.test(trimmedValue)) {
        message.error(`Please enter a valid${colName} number.`);
        return false;
    } else {
        return true;
    }
}

export const depthValueValidator = (from: string, to: string, isPlod: boolean): boolean => {
    if (!from && !to) return true;

    const colTitle = isPlod ? 'Machine Hours ' : ''

    if (!numberValidator(from, `${colTitle}From`) || !numberValidator(to, `${colTitle}To`)) {
        message.error(`The "${colTitle}From" and "${colTitle}To" values must both be numbers or both empty.`);
        return false;
    }

    const fromValue = parseFloat(from);
    const toValue = parseFloat(to);

    if (fromValue < 0 || toValue < 0 ) {
        message.error(`The "${colTitle}From" and "${colTitle}To" values must be greater than 0.`);
        return false;
    }

    if (fromValue > toValue) {
        message.error(`The "${colTitle}From" value must be not greater than the "${colTitle}To" value.`);
        return false;
    }

    return true;
};

export const clockTimeValidator = (time: string): boolean => {
    if (!time) return false;

    const trimmedTime = time.trim().replaceAll(' ', '');

    const timePattern = /^(1[0-2]|0?[1-9]):([0-5][0-9])(AM|PM)$/i;
    if (!timePattern.test(trimmedTime)) return false;

    const [hoursMinutesStr, period] = trimmedTime.split(/(AM|PM)/i);
    const [hoursStr, minutesStr] = hoursMinutesStr.split(':');
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);

    if (hours < 1 || hours > 12 || minutes < 0 || minutes > 59) return false;

    return true;
};

export const clockTimeValidator24Hours = (time: string): boolean => {
    if (!time) return false;

    const trimmedTime = time.trim().replaceAll(' ', '');

    const timePattern = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
    if (!timePattern.test(trimmedTime)) return false;

    const [hoursStr, minutesStr] = trimmedTime.split(':');
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);

    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return false;

    return true;
};

export const parseClockTimeToDate = (time: string): Date | null => {
    if (!time) return null;

    const trimmedTime = time.trim().replaceAll(' ', '');
    const is12Hour = /(AM|PM)$/i.test(trimmedTime);

    if (is12Hour) {
        if (!clockTimeValidator(trimmedTime)) return null;

        const [hoursMinutesStr, period] = trimmedTime.split(/(AM|PM)/i);
        const [hoursStr, minutesStr] = hoursMinutesStr.split(':');
        let hours = parseInt(hoursStr, 10);
        const minutes = parseInt(minutesStr, 10);

        if (period.toUpperCase() === 'PM' && hours !== 12) {
            hours += 12;
        } else if (period.toUpperCase() === 'AM' && hours === 12) {
            hours = 0;
        }

        const now = new Date();
        now.setHours(hours, minutes, 0, 0);
        return now;
    } else {
        if (!clockTimeValidator24Hours(trimmedTime)) return null;

        const [hoursStr, minutesStr] = trimmedTime.split(':');
        const hours = parseInt(hoursStr, 10);
        const minutes = parseInt(minutesStr, 10);

        const now = new Date();
        now.setHours(hours, minutes, 0, 0);
        return now;
    }
};

export const formatDateToClockTime = (date: Date | null):string => {
    if (!(date instanceof Date) || isNaN(date.getTime())) return '';

    const hours = date.getHours();
    const minutes = date.getMinutes();

    const period = hours >= 12 ? 'PM' : 'AM';
    const adjustedHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const formattedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
    return `${adjustedHours}:${formattedMinutes} ${period}`;
};

export const calculateDurationBetweenDates = (startDate: Date | null, finishDate: Date | null): string => {
    if (!(startDate instanceof Date) || !(finishDate instanceof Date) ||
        isNaN(startDate.getTime()) || isNaN(finishDate.getTime())) {
        return '';
    }

    let startMs = startDate.getTime();
    let finishMs = finishDate.getTime();

    if (finishMs < startMs) {
        finishMs += 24 * 60 * 60 * 1000;
    }

    const durationMs = finishMs - startMs;
    const totalMinutes = Math.floor(durationMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    const formattedHours = hours < 10 ? `0${hours}` : `${hours}`;
    const formattedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
    return `${formattedHours}:${formattedMinutes}`;
};


export const calculateDurationHours = (startDate: Date | null, finishDate: Date | null): string => {
    if (!(startDate instanceof Date) || !(finishDate instanceof Date) ||
        isNaN(startDate.getTime()) || isNaN(finishDate.getTime())) {
        return '';
    }

    let startMs = startDate.getTime();
    let finishMs = finishDate.getTime();

    if (finishMs < startMs) {
        finishMs += 24 * 60 * 60 * 1000;
    }

    const durationMs = finishMs - startMs;
    const totalMinutes = Math.floor(durationMs / (1000 * 60));
    const totalHours = totalMinutes / 60;

    //custom round:
   /* const blocks = Math.floor(totalMinutes / 15);
    const remainder = totalMinutes % 15;
    const roundedMinutes = remainder < 5 ? blocks * 15 : (blocks + 1) * 15;
    const totalHours = roundedMinutes / 60;*/

    return totalHours.toFixed(2);
};

export const formatTotalActivityHours = (value: number): string => {
    if (typeof value !== 'number' || isNaN(value)) return '';

    const snapped = (value >= 11.8 && value <= 12.2)
        ? 12
        : Math.round(value * 100) / 100;

    return Number.isInteger(snapped)
        ? snapped.toString()
        : snapped.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
};

export const WorkingTimeValueValidator =  (start: string, finish: string):boolean => {
    if (!start && !finish) return true;

    if (!start || !finish) {
        message.error('One of "Time Start" or "Time Finish" value is empty!');
        return false;
    }

    if ((!clockTimeValidator(start) && !clockTimeValidator24Hours(start)) || (!clockTimeValidator(finish) && !clockTimeValidator24Hours(finish))) {
        message.error('Time value should be in the format hh:mm; like 0:00 for 0am/12pm and 18:00 for 6pm');
        return false;
    }
    return true;
};

export const getIsDayShift = (shift: string | null | undefined): boolean => {
    if (shift === 'Day') return true;
    if (shift === 'Night') return false;
    const isDayShift = new Date().getHours() >= 6 && new Date().getHours() < 18
    return isDayShift;
};

export const dateValidator = (dateString: string): boolean => {
    if (!dateString) return false;

    const trimmedDate = dateString.trim();

    const datePattern = /^(\d{2})-([A-Za-z]{3})-(\d{2})$/;
    if (!datePattern.test(trimmedDate)) {
        message.error('The date must be in the format "dd-Mmm-yy" (e.g., "25-Oct-24").');
        return false;
    }

    const [dayStr, monthStr, yearStr] = trimmedDate.split('-');
    const day = parseInt(dayStr, 10);
    const year = parseInt(yearStr, 10);

    const validMonths = [
        'jan', 'feb', 'mar', 'apr', 'may', 'jun',
        'jul', 'aug', 'sep', 'oct', 'nov', 'dec'
    ];
    const monthLower = monthStr.toLowerCase();
    const monthIndex = validMonths.indexOf(monthLower);

    if (monthIndex === -1) {
        message.error('The month must be a valid three-letter abbreviation (e.g., "Jul", "Sep").');
        return false;
    }

    const fullYear = 2000 + year;
    const daysInMonth = new Date(fullYear, monthIndex + 1, 0).getDate();
    if (day < 1 || day > daysInMonth) {
        message.error(`The day must be between 1 and ${daysInMonth} for ${monthStr}-${yearStr}.`);
        return false;
    }

    return true;
};